import { useEffect, useState } from "react";
import { Gift, EyeOff, CheckCircle2, Clock, HeartCrack, Heart } from "lucide-react";
import { api } from "@/lib/api";

// Dettaglio giocate di un partecipante Survival, raggruppate per giornata.
// Versione INLINE (nessun overlay): resa subito sotto la riga cliccata in
// classifica, così è immediatamente visibile senza dover risalire.
export function SurvivaPicksContent({ tid, userId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    setData(null); setErr(null);
    api(`/sv/tournaments/${tid}/participants/${userId}/picks`)
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setErr(e.message || "Errore"); });
    return () => { alive = false; };
  }, [tid, userId]);

  return (
    <div className="space-y-2" data-testid="sv-picks-inline">
      {err && <div className="rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 p-3 text-sm text-[#EF4444]">{err}</div>}
      {!data && !err && <div className="py-6 text-center text-[#94A3B8] text-sm">Caricamento...</div>}
      {data && data.matchdays.length === 0 && <div className="text-sm text-[#64748B] italic">Nessuna giornata giocata.</div>}
      {(() => {
        if (!data) return null;
        const rel = data.matchdays.filter((m) => m.settled || m.deadline_passed || (m.picks && m.picks.length > 0));
        if (rel.length === 0) return <div className="text-sm text-[#64748B] italic">Nessuna giocata ancora visibile.</div>;
        return rel.map((md) => (
          <div key={md.matchday_id} data-testid={`sv-inline-md-${md.matchday}`} className="rounded-xl border border-white/10 bg-[#0F1216] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-sm">Giornata {md.matchday}</span>
                {typeof md.lives_at_start === "number" && (
                  <span
                    data-testid={`sv-inline-lives-${md.matchday}`}
                    title={`Vite a inizio giornata: ${md.lives_at_start}`}
                    className="inline-flex items-center gap-0.5"
                  >
                    {md.lives_at_start > 0 ? (
                      Array.from({ length: Math.min(md.lives_at_start, 6) }).map((_, hi) => (
                        <Heart key={hi} size={13} className="text-[#EF4444] fill-[#EF4444]" />
                      ))
                    ) : (
                      <span className="text-[10px] text-[#94A3B8] font-bold">0 vite</span>
                    )}
                    {md.lives_at_start > 6 && (
                      <span className="text-[10px] text-[#EF4444] font-bold ml-0.5">+{md.lives_at_start - 6}</span>
                    )}
                  </span>
                )}
                {md.big_match_bonus_won && (
                  <span title="Big Match Bonus: +1 vita" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]"><Gift size={12} className="text-[#F59E0B]" /></span>
                )}
              </div>
              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded ${md.settled ? "bg-[#00D95F]/20 text-[#00D95F]" : "bg-white/10 text-[#94A3B8]"}`}>
                {md.settled ? "Calcolata" : md.deadline_passed ? "Chiusa" : "Aperta"}
              </span>
            </div>

            {md.hidden ? (
              <div className="flex items-center gap-2 rounded-lg bg-[#181D22] border border-white/10 p-2.5 text-xs text-[#94A3B8] italic">
                <EyeOff size={14} /> Pronostici nascosti finché il timer non scade
              </div>
            ) : (md.picks && md.picks.length > 0) ? (
              <div className="space-y-1">
                {md.picks.map((p, i) => {
                  const decided = p.correct === true || p.correct === false;
                  // Esito per SINGOLA partita: se ha già un risultato mostra
                  // verde/rosso anche in calcolo PARZIALE; se non deciso è
                  // "in attesa" (orologio), tranne le sospese di una giornata
                  // già chiusa che restano valide (verde + badge SOSPESA).
                  const outcome = decided ? (p.correct === false ? "ko" : "ok") : (md.settled ? "ok" : "na");
                  const suspended = md.settled && !decided;
                  const hasScore = p.home_score !== null && p.home_score !== undefined && p.away_score !== null && p.away_score !== undefined;
                  return (
                    <div key={i} className="flex items-center gap-2 py-1">
                      <span className="flex-1 min-w-0 truncate text-sm flex items-center gap-1.5">
                        {p.home_team} <span className="text-[#94A3B8]">-</span> {p.away_team}
                        {suspended && <span title="Partita sospesa: giocata valida" className="text-[9px] px-1.5 py-0.5 rounded bg-[#00D95F]/15 text-[#00D95F] border border-[#00D95F]/40 font-bold shrink-0">SOSPESA</span>}
                      </span>
                      {hasScore ? (
                        <span data-testid={`sv-inline-result-${i}`} title="Risultato finale" className="min-w-11 text-center px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-xs font-bold text-[#94A3B8] shrink-0">{p.home_score}-{p.away_score}</span>
                      ) : suspended ? (
                        <span className="min-w-11 text-center text-xs text-[#94A3B8] shrink-0">—</span>
                      ) : null}
                      <span className={`min-w-7 text-center px-2 py-0.5 rounded border text-sm font-extrabold ${outcome === "ok" ? "border-[#00D95F] bg-[#00D95F]/15 text-[#00D95F]" : outcome === "ko" ? "border-[#EF4444] bg-[#EF4444]/15 text-[#EF4444]" : "border-white/15 text-white"}`}>{p.pick}</span>
                      <span className="w-6 flex items-center justify-center">
                        {outcome === "ok" && <CheckCircle2 size={18} className="text-[#00D95F]" />}
                        {outcome === "ko" && <HeartCrack size={18} className="text-[#EF4444]" />}
                        {outcome === "na" && <Clock size={15} className="text-[#94A3B8]" />}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-[#64748B] italic">Nessuna scelta inviata.</div>
            )}
          </div>
        ));
      })()}
    </div>
  );
}
